import {
  collection,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import { OperatingLocationSchema, type OperatingLocation } from '@etiquetachef/types';
import { reportNonFatal, serializeError } from './errorReport';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'firestoreOperatingLocations';

/** Teto de células lidas por tenant. Um tenant opera em poucas; 100 sobra. */
export const OPERATING_LOCATIONS_LIMIT = 100;

/** Célula do agregado já com os carimbos de servidor convertidos para millis. */
export type OperatingLocationRow = OperatingLocation & {
  firstSeenAtMillis: number | null;
  lastSeenAtMillis: number | null;
};

export type OperatingLocationsResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; rows: OperatingLocationRow[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Converte um doc de `operating_locations` (já normalizado por `snapshotData`,
 * Timestamp → millis) numa linha da tabela. Pura; `null` se fora do schema.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200.
 */
export function toOperatingLocationRow(raw: Record<string, unknown>): OperatingLocationRow | null {
  const parsed = OperatingLocationSchema.safeParse(raw);
  if (!parsed.success) return null;
  const millis = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  return {
    ...parsed.data,
    firstSeenAtMillis: millis(raw.firstSeenAt),
    lastSeenAtMillis: millis(raw.lastSeenAt),
  };
}

/**
 * Escuta as células "onde o tenant opera" — posições do aparelho registradas
 * na impressão e na gravação NFC, agregadas pelo trigger.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200 (owner: a equipe precisa saber onde
 * cada tenant opera de fato).
 *
 * Contrato: `tenants/{tenantId}/operating_locations`, mais recentes primeiro
 * (`orderBy('lastSeenAt')` em campo único — dispensa índice composto), até
 * `OPERATING_LOCATIONS_LIMIT`. Doc fora do schema sai da lista COM
 * `reportNonFatal` (não temos DevTools do operador). Erro do listener vira
 * `ERROR`.
 *
 * Usado por: `features/tenants/OperatingLocationsSection.tsx` (aba Local do
 * `TenantDetailPage`).
 *
 * Arquitetura: só super-admin lê (rules `operating_locations`); só o trigger
 * `onLabelWritten` escreve (`apps/functions/src/lib/operatingLocations.ts`).
 *
 * Dados: ver `OperatingLocationSchema` em `packages/types/src/labelLocation.ts`
 * — `firstSeenAt`/`lastSeenAt` são serverTimestamp, chegam aqui em millis.
 */
export function subscribeOperatingLocations(
  tenantId: string,
  onResult: (r: OperatingLocationsResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'operating_locations');
  const q = query(ref, orderBy('lastSeenAt', 'desc'), fsLimit(OPERATING_LOCATIONS_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const rows: OperatingLocationRow[] = [];
      for (const d of snap.docs) {
        const row = toOperatingLocationRow(snapshotData(d));
        if (row) {
          rows.push(row);
        } else {
          reportNonFatal('admin_operating_location_parse_failed', { tenantId, cell: d.id });
        }
      }
      onResult({ kind: 'READY', rows });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      reportNonFatal('admin_operating_locations_listen_failed', { tenantId, ...serializeError(err) });
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Distância em km entre dois pontos (haversine, raio médio 6371 km). Precisão
 * de sobra para "a célula está longe do endereço declarado?".
 */
export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const rad = (d: number): number => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}
